import React, { useState } from 'react';
import { 
  Calendar, 
  Users, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Clock,
  User
} from 'lucide-react';
import useAxiosInstance from '../../lib/useAxiosInstance';
import { toast,ToastContainer } from 'react-toastify';
import { useQueryClient, useMutation } from 'react-query';
import { Tooltip } from 'antd';
import "../../Styles/overview2.css";
import "../../Styles/overView.css";

const PendingApprovals = ({ onToggleView, events,onEventClick,setCalendarPrompt }) => {
  const axiosInstance = useAxiosInstance()
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem("user"));
  const [activeTab, setActiveTab] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getRelativeTime = (createdAt) => {
    if (!createdAt) return 'some time ago';
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays >= 1) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours >= 1) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  };

  const approvalItems = events
  .map(event => ({
    id: event._id,
    title: event?.eventTitle,
    submittedBy: event?.createdByUserName || 'Unknown',
    submittedTime: getRelativeTime(event.createdAt),
    createdAt: new Date(event.createdAt), 
    type: event.typeOfEvent?.toLowerCase() || 'event',
    status: event.status,
    description: event.description || '',
    startTime: event.start,  
    endTime: event.end   
  }))
  .sort((a, b) => b.createdAt - a.createdAt);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'appointment':
        return <Users className="w-4 h-4 text-blue-400" />;
      case 'event':
        return <Calendar className="w-4 h-4 text-purple-400" />;
      case 'document':
        return <FileText className="w-4 h-4 text-green-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const filteredItems = approvalItems.filter(item => item.status === activeTab);
  const pageCount = Math.ceil(filteredItems.length / itemsPerPage);

  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

const updateEventStatus = async ({ id, status }) => {
  if (!user?._id || !user?.firstName || !user?.lastName) {
    throw new Error("Missing user information in localStorage");
  }

  let payload;

  if (status === "approved") {
    payload = {
      isApproved: true,
      approvedBy: {
        _id: user._id,
        name: `${user.firstName} ${user.lastName}`,
      },
    };
  } else if (status === "pending") {
    payload = {
      status: "pending",
    };
  } else {
    payload = {
      status: "cancelled",
    };
  }

  const res = await axiosInstance.put(`api/eventScheduler/events/${id}`, payload);
  if (!res?.data?.status) throw new Error(res?.data?.message || "Failed to update event");
  return res.data.data;          
};

const {
  mutate: updateEventStatusMutation,   
  isLoading: isUpdatingStatus,
} = useMutation(updateEventStatus, {
  /** ------------------------------
   * 1. Optimistically update cache
   * -----------------------------*/
  onMutate: async ({ id, status }) => {
    await queryClient.cancelQueries(["Event-List"]); // pause outgoing refetch

    const previous = queryClient.getQueryData(["Event-List"]);

    // patch the cache so UI updates instantly
    queryClient.setQueryData(["Event-List"], (old) => {
      if (!old?.status) return old;
      return {
        ...old,
        data: {
          ...old.data,
          events: old.data.events.map((ev) =>
            ev._id === id
              ? {
                  ...ev,
                  isApproved: status === "approved" ? true : false,
                  approvedBy:
                    status === "approved"
                      ? { _id: user._id, name: `${user.firstName} ${user.lastName}` }
                      : status === "pending"
                        ? undefined
                        : ev.approvedBy,
                }
              : ev
          ),
        },
      };
    });
    return { previous };
  },

  /** ------------------------------
   * 2. Roll back if the call fails
   * -----------------------------*/
  onError: (error, _vars, ctx) => {
    queryClient.setQueryData(["Event-List"], ctx.previous); // rollback
    toast.error(error?.message || "Failed to update event");
  },

  /** ------------------------------
   * 3. Always re‑fetch for freshness
   * -----------------------------*/
  onSettled: () => {
    queryClient.invalidateQueries(["Event-List"]); // server‑truth refetch
  },

  /** ------------------------------
   * 4. User feedback
   * -----------------------------*/
  onSuccess: () => {
    toast.success("Status updated successfully");
  },
});


const handleUpdateStatus = async (id, status) => {
  await updateEventStatusMutation({ id, status });

  // const approvedEvent = events.find(e => e._id === id);
  // if (!approvedEvent) return;

  // Don't touch Google Calendar if it's a visit
  // if (approvedEvent.typeOfEvent === 'visit') return;

  // try {
  //   // const approver = user;
  //   // const attendeesEmails = approvedEvent.attendees?.map(a => a.email).filter(Boolean) || [];

  //   // if (status === 'approved') {
  //   //   await axiosInstance.post('api/eventScheduler/add-event-google', {
  //   //     title: approvedEvent.eventTitle,
  //   //     description: approvedEvent?.description,
  //   //     start: approvedEvent?.start,
  //   //     end: approvedEvent?.end,
  //   //     attendees: attendeesEmails,
  //   //     // organizerEmail: approver.email,
  //   //     organizerEmail: approvedEvent?.email,
  //   //   });
  //   // }

  //   // ✅ Remove from Google Calendar on disapproval
  //   // Ask user for Google Calendar sync
  //   if (status === 'approved') {
  //     setCalendarPrompt({
  //       visible: true,
  //       eventId: id,
  //       eventData: approvedEvent
  //     });
  //   }
    
  //   else if (status === 'pending' && approvedEvent.googleEventId) {
  //     await axiosInstance.post('api/eventScheduler/remove-event-google', {
  //       googleEventId: approvedEvent.googleEventId
  //     });
  //   }

  // } catch (err) {
  //   console.error("Google Calendar Sync Failed", err);
  //   toast.error("Google Calendar sync issue");
  // }
};


  return (
    <div className="bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 shadow-lg overflow-hidden">
      
      {/* Header */}
      <div className="p-6 border-b border-white/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-purple-500/10 rounded-xl backdrop-blur-sm">
              <AlertCircle className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Approvals</h2>
          </div>
          <button
            onClick={onToggleView}
            className="flex items-center space-x-2 px-4 py-2 bg-[var(--polstrat-primary)] text-white rounded-lg hover:bg-[#003558] transition-all duration-200"
          >
            <Calendar className="w-4 h-4" />
            <span>View Calendar</span>
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-1 bg-white/20 backdrop-blur-sm rounded-lg p-1">
          <button
            onClick={() => {
              setActiveTab('pending');
              setCurrentPage(1);
            }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 flex-1 justify-center ${
              activeTab === 'pending'
                ? 'bg-white/30 shadow-md text-gray-800'
                : 'text-gray-600 hover:bg-white/20'
            }`}
          >
            <Clock className="w-4 h-4 text-orange-500" />
            <span className="font-medium text-gray-800">Pending</span>
            <span className="bg-orange-500/20 text-orange-700 px-2 py-1 rounded-full text-xs font-bold">
              {approvalItems.filter(item => item.status === 'pending').length}
            </span>
          </button>
          <button
           onClick={() => {
              setActiveTab('approved');
              setCurrentPage(1);
            }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 flex-1 justify-center ${
              activeTab === 'approved'
                ? 'bg-white/30 shadow-md text-gray-800'
                : 'text-gray-600 hover:bg-white/20'
            }`}
          >
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="font-medium text-gray-800">Approved</span>
            <span className="bg-green-500/20 text-green-700 px-2 py-1 rounded-full text-xs font-bold">
              {approvalItems.filter(item => item.status === 'approved').length}
            </span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 bg-transparent">
        <div className="space-y-4">
          {paginatedItems.map((item) => (
            <div 
            key={item.id} 
            className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 shadow-md transition-all duration-300 transform hover:scale-[1.01] hover:shadow-xl hover:border-blue-300/40 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1 " onClick={() => onEventClick(item.id)}>
                  <div className="flex-shrink-0 pt-1">
                    <span
                      className={`inline-block w-3 h-3  rounded-full ${
                        item.type === 'appointment'
                          ? 'bg-blue-500'
                          : item.type === 'invitation'
                          ? 'bg-purple-500'
                          : item.type === 'visit'
                          ? 'bg-green-500'
                          : 'bg-gray-400'
                      }`}
                    ></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-800 mb-1">{item.title}</h4>
                    <span
                      className={`inline-block text-xs font-semibold px-2 py-1 rounded-full mb-2 ${
                        item.type === 'appointment'
                          ? 'bg-blue-100 text-blue-600'
                          : item.type === 'invitation'
                          ? 'bg-purple-100 text-purple-600'
                          : item.type === 'visit'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    </span>
                    <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-600">
                      <div className="flex items-center space-x-1">
                        <User className="w-3 h-3" />
                        <span>Submitted by {item.submittedBy}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{item.submittedTime}</span>
                      </div>
                    <div className="flex items-center space-x-1">
                      {item.startTime && (
                      <div className="text-sm text-gray-600 flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>
                          {new Date(item.startTime).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span>
                          {new Date(item.startTime).toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) === '12:00 AM' && !item.endTime
                            ? 'All Day'
                            : `${new Date(item.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${new Date(item.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                          }
                        </span>
                      </div>
                    )}
                    </div>
                    
                    </div>
                  </div>
                </div>
                
                {activeTab === 'pending'  && user?.role!=='data_entry_operator' && (
                  <div className="flex space-x-2 ml-4">
                    <Tooltip title="Approve">
                      <button 
                        className="p-2 hover:bg-green-500/20 rounded-lg transition-colors group"
                        onClick={() => handleUpdateStatus(item.id, 'approved')}
                        >
                          <CheckCircle className="w-5 h-5 text-green-400 group-hover:text-green-500" />
                      </button>
                    </Tooltip>
                     <Tooltip title="Cancel">
                      <button 
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group"
                      onClick={() => handleUpdateStatus(item.id, 'cancelled')}
                      >
                        <XCircle className="w-5 h-5 text-red-400 group-hover:text-red-500" />
                      </button>
                     </Tooltip>
                    
                  </div>
                  
                )}

                {activeTab === 'approved' && user?.role!=='data_entry_operator' && (
                  <div className="flex space-x-2 ml-4">
                    <Tooltip title="Disapprove">
                        <button 
                          className="p-2 hover:bg-yellow-500/20 rounded-lg transition-colors group"
                          onClick={() => handleUpdateStatus(item.id, 'pending')}
                        >
                          <XCircle className="w-5 h-5 text-yellow-500 group-hover:text-yellow-600" />
                        </button>
                    </Tooltip>
                    
                    <div className="flex items-center space-x-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">Approved</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {pageCount > 1 && (
  <div className="flex justify-center mt-6 space-x-2">
    {Array.from({ length: pageCount }).map((_, index) => (
      <button
        key={index}
        onClick={() => setCurrentPage(index + 1)}
        className={`px-3 py-1 rounded-md text-sm font-medium ${
          currentPage === index + 1
            ? 'bg-blue-500 text-white'
            : 'bg-white/20 text-gray-700 hover:bg-white/30'
        }`}
      >
        {index + 1}
      </button>
    ))}
  </div>
)}
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-200/50 rounded-full flex items-center justify-center mx-auto mb-4">
                {activeTab === 'pending' ? (
                  <Clock className="w-8 h-8 text-gray-400" />
                ) : (
                  <CheckCircle className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No {activeTab} items
              </h3>
              <p className="text-gray-500">
                {activeTab === 'pending' 
                  ? 'All caught up! No pending approvals at the moment.'
                  : 'No approved items to display.'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* {calendarPrompt.visible && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-lg p-6 w-96 text-center space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">
        Add to Google Calendar?
      </h3>
      <p className="text-sm text-gray-600">
        Do you want to add this {calendarPrompt.eventData?.typeOfEvent || 'event'} to your Google Calendar?
      </p>
      <div className="flex justify-center gap-4 mt-4">
        <button
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          onClick={confirmCalendarSync}
        >
          Yes
        </button>
        <button
          className="px-4 py-2 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
          onClick={cancelCalendarSync}
        >
          No
        </button>
      </div>
    </div>
  </div>
)} */}
    </div>
  );
};

export default PendingApprovals;
